import Splide from '@splidejs/splide';

const BREAKPOINT_DESKTOP = 1024;

class LyleQuoteGallery extends HTMLElement {
  #splide = null;
  #mediaQuery = null;
  #removedSlide = null;
  #onMediaChange = null;

  connectedCallback() {
    this.#mediaQuery = window.matchMedia(`(max-width: ${BREAKPOINT_DESKTOP - 1}px)`);
    this.#init();
  }

  disconnectedCallback() {
    this.#splide?.destroy();
    if (this.#onMediaChange) {
      this.#mediaQuery?.removeEventListener('change', this.#onMediaChange);
      this.#onMediaChange = null;
    }
    this.#splide = null;
    this.#mediaQuery = null;
    this.#removedSlide = null;
  }

  #init() {
    const carousel = this.querySelector('[data-quote-gallery-carousel]');
    if (!carousel) return;

    const slideCount = carousel.querySelectorAll('.splide__slide').length;
    if (slideCount === 0) return;

    this.#setSlideWidths();

    const splide = new Splide(carousel, {
      arrows: false,
      autoWidth: true,
      drag: slideCount > 1,
      gap: 'var(--spacing-xs)',
      mediaQuery: 'min',
      padding: 'var(--spacing-xs)',
      pagination: false,
      type: 'slide',
      breakpoints: {
        [BREAKPOINT_DESKTOP]: {
          gap: 'var(--spacing-md)',
          perMove: 1,
        },
      },
    });

    this.#splide = splide;
    splide.mount();

    this.#bindNav();
    this.#bindProgress();
    this.#toggleFirstSlide(this.#mediaQuery.matches);
    this.#syncArrowWidth();
    this.#syncProgress();
    this.#checkArrows(0);

    this.#splide.on('moved', () => this.#syncArrowWidth());

    this.#onMediaChange = (e) => {
      this.#setSlideWidths();
      this.#toggleFirstSlide(e.matches);
      this.#syncArrowWidth();
      this.#splide.refresh();
      this.#syncProgress();
      this.#checkArrows(this.#splide.index);
    };
    this.#mediaQuery.addEventListener('change', this.#onMediaChange);
  }

  #setSlideWidths() {
    const isMobile = this.#mediaQuery.matches;
    for (const slide of this.querySelectorAll('.splide__slide')) {
      const width = isMobile ? slide.dataset.mobileWidth : slide.dataset.desktopWidth;
      if (width) slide.style.width = `${width}px`;
    }
  }

  #syncArrowWidth() {
    const slide = this.querySelector('.splide__slide.is-active') || this.querySelector('.splide__slide');
    if (!slide) return;

    const width = slide.getBoundingClientRect().width || Number.parseFloat(slide.style.width);
    if (width) this.style.setProperty('--quote-gallery-slide-width', `${width}px`);
  }

  #bindNav() {
    const prev = this.querySelector('[data-quote-gallery-prev]');
    const next = this.querySelector('[data-quote-gallery-next]');
    prev?.addEventListener('click', () => this.#splide.go('<'));
    next?.addEventListener('click', () => this.#splide.go('>'));
  }

  #getEnd() {
    return this.#splide?.Components?.Controller?.getEnd() ?? 0;
  }

  #bindProgress() {
    const progressEl = this.querySelector('.quote-gallery__progress');
    const bar = this.querySelector('[data-quote-gallery-progress]');
    if (!bar) return;

    if (this.#splide.length <= 1) {
      if (progressEl) progressEl.hidden = true;
      return;
    }

    this.#splide.on('move', (index) => {
      this.#checkArrows(index);
      const end = this.#getEnd();
      bar.style.width = `${((index + 1) / (end + 1)) * 100}%`;
    });
  }

  #syncProgress() {
    const bar = this.querySelector('[data-quote-gallery-progress]');
    if (!bar || !this.#splide || this.#splide.length <= 1) return;
    const end = this.#getEnd();
    bar.style.width = `${((this.#splide.index + 1) / (end + 1)) * 100}%`;
  }

  #checkArrows(index) {
    if (!this.#splide) return;
    const prev = this.querySelector('[data-quote-gallery-prev]');
    const next = this.querySelector('[data-quote-gallery-next]');
    this.#setArrowHidden(prev, index === 0);
    this.#setArrowHidden(next, index === this.#getEnd());
  }

  #setArrowHidden(btn, hidden) {
    if (!btn) return;
    btn.classList.toggle('quote-gallery__arrow--hidden', hidden);
    btn.inert = hidden;
  }

  #toggleFirstSlide(isMobile) {
    if (!this.#splide) return;

    const container = this.querySelector('.quote-gallery__container');
    const firstSlide = this.querySelector('.splide__slide');
    const isQuoteSlide = firstSlide?.classList.contains('quote-gallery__quote-slide');

    if (isMobile) {
      if (firstSlide && isQuoteSlide && !this.#removedSlide) {
        if (this.#splide.length <= 1) {
          // Only the quote slide exists — hide the carousel rather than leaving Splide empty.
          // The featured quote section already shows this content on mobile.
          if (container) container.hidden = true;
          return;
        }
        const currentIndex = this.#splide.index;
        this.#removedSlide = firstSlide;
        this.#splide.remove(0);
        this.#splide.go(Math.max(0, currentIndex - 1));
        this.#syncDrag();
      }
    } else {
      if (container) container.hidden = false;
      if (this.#removedSlide) {
        const currentIndex = this.#splide.index;
        this.#splide.add(this.#removedSlide, 0);
        this.#splide.go(currentIndex + 1);
        this.#removedSlide = null;
        this.#syncDrag();
      }
    }
  }

  #syncDrag() {
    if (!this.#splide) return;
    this.#splide.options = { drag: this.#splide.length > 1 };
  }
}

if (!customElements.get('lyle-quote-gallery')) {
  customElements.define('lyle-quote-gallery', LyleQuoteGallery);
}
